import React, { useState, useEffect, useMemo } from "react";
import styled from "@emotion/styled";
import {
  Box,
  Card,
  Typography,
  Grid,
  Stack,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CardContent,
  Chip,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Skeleton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import StorageIcon from "@mui/icons-material/Storage";
import { useLazyGetDataRequestQuery, useLazyGetDataRequestCSVQuery } from "api/homeApi";
import BusyLoader from "components/BusyLoader";
import { METRICS, LOCATIONS, getMetricLabel, getLocationLabel } from "utils/dataRequestConfig";

// Styled components matching Dashboard style
const DrawerHeader = styled("Box")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme?.spacing ? theme.spacing(0, 1) : "0 8px",
  ...theme?.mixins?.toolbar,
}));

const PageTitle = styled(Typography)({
  fontFamily: "Montserrat",
  fontWeight: 700,
  fontSize: "20px",
  color: "#0a2240",
  marginTop: "1rem",
  marginBottom: "1rem",
});

const FilterCard = styled(Card)({
  backgroundColor: "#ffffff",
  borderRadius: "14px",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  marginBottom: "24px",
  "& .MuiCardContent-root": {
    padding: "24px",
  },
});

const DataCard = styled(Card)({
  backgroundColor: "#ffffff",
  borderRadius: "14px",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  height: "100%",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 6px 16px rgba(0, 0, 0, 0.15)",
    transform: "translateY(-2px)",
  },
  "& .MuiCardContent-root": {
    padding: "20px",
  },
});

const SectionLabel = styled(Typography)({
  fontFamily: "Montserrat",
  fontWeight: 600,
  fontSize: "0.7rem",
  color: "#666",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginBottom: "8px",
});

const StyledChip = styled(Chip)(({ variant }) => ({
  fontFamily: "Montserrat",
  fontWeight: 600,
  borderRadius: variant === "rounded" ? "999px" : "6px",
}));

const HostToggleGroup = styled(ToggleButtonGroup)({
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  "& .MuiToggleButton-root": {
    borderRadius: "20px",
    textTransform: "none",
    fontFamily: "Montserrat",
    fontWeight: 500,
    fontSize: "0.875rem",
    border: "1px solid rgba(0, 0, 0, 0.12)",
    padding: "6px 16px",
    "&.Mui-selected": {
      backgroundColor: "#0a2240",
      color: "#fff",
      "&:hover": {
        backgroundColor: "#0d2d52",
      },
    },
  },
});

// Loading skeleton component
const DataCardsLoadingSkeleton = () => (
  <Grid container spacing={3}>
    {[1, 2, 3, 4, 5, 6].map((item) => (
      <Grid item xs={12} sm={6} lg={4} key={item}>
        <Card sx={{ borderRadius: "14px", p: 2 }}>
          <Stack spacing={2}>
            <Skeleton variant="rectangular" height={32} width="60%" />
            <Skeleton variant="rectangular" height={1} />
            <Skeleton variant="rectangular" height={24} width="80%" />
            <Skeleton variant="rectangular" height={1} />
            <Skeleton variant="rectangular" height={100} />
          </Stack>
        </Card>
      </Grid>
    ))}
  </Grid>
);

export const DataRequest = ({ user, features }) => {
  const [tagValue, setTagValue] = useState("");
  const [groupId, setGroupId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedHost, setSelectedHost] = useState("All");
  const [responseData, setResponseData] = useState(null);

  const [getData, { data: apiData, isLoading, isError, error }] = useLazyGetDataRequestQuery();
  const [getCSV, { isLoading: isDownloading }] = useLazyGetDataRequestCSVQuery();

  useEffect(() => {
    if (apiData) {
      setResponseData(apiData);
      setSelectedHost("All");
    }
  }, [apiData]);

  const handleSearch = () => {
    if (tagValue && groupId) {
      getData({ tag_value: tagValue, group_id: groupId });
    }
  };

  const handleDownloadCSV = async () => {
    if (tagValue && groupId) {
      try {
        const result = await getCSV({ tag_value: tagValue, group_id: groupId }).unwrap();
        
        // Create blob and download
        const url = window.URL.createObjectURL(result);
        const link = document.createElement("a");
        link.href = url;
        link.download = `data_${tagValue}_${groupId}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Error downloading CSV:", err);
      }
    }
  };

  // Get unique hosts from data
  const uniqueHosts = useMemo(() => {
    if (!responseData?.data) return [];
    const hosts = [...new Set(responseData.data.map((item) => item.host))];
    return ["All", ...hosts.sort()];
  }, [responseData]);

  // Filter data based on search and host
  const filteredData = useMemo(() => {
    if (!responseData?.data) return [];
    
    return responseData.data.filter((item) => {
      // Host filter
      const hostMatch = selectedHost === "All" || item.host === selectedHost;
      
      // Search filter
      if (!searchText) return hostMatch;
      
      const searchLower = searchText.toLowerCase();
      const identityMatch = item.identity?.toLowerCase().includes(searchLower);
      const hostNameMatch = item.host?.toLowerCase().includes(searchLower);
      
      // Handle both formats: with identity_key (metrics array) and without (metric/value)
      let metricsMatch = false;
      if (item.metrics) {
        // Format with identity_key
        metricsMatch = item.metrics.some(
          (m) =>
            m.name?.toLowerCase().includes(searchLower) ||
            m.value?.toString().toLowerCase().includes(searchLower)
        );
      } else {
        // Format without identity_key
        metricsMatch =
          item.metric?.toLowerCase().includes(searchLower) ||
          item.value?.toString().toLowerCase().includes(searchLower);
      }
      
      return hostMatch && (identityMatch || hostNameMatch || metricsMatch);
    });
  }, [responseData, selectedHost, searchText]);

  return (
    <Box
      sx={{
        flexGrow: 1,
        backgroundColor: "#F4F7FE",
        minHeight: "100vh",
        padding: { xs: "0.5rem", sm: "0" },
      }}
    >
      <DrawerHeader />

      <PageTitle sx={{ paddingLeft: { xs: "0.3rem", sm: "1.3vw" } }}>
        SU Data Requests
      </PageTitle>

      {/* Filter Section */}
      <Box sx={{ padding: { xs: "0.3rem", sm: "1.3vw" }, paddingTop: 0 }}>
        <FilterCard>
          <CardContent>
            <Grid container spacing={3} alignItems="flex-end">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel shrink>Metric</InputLabel>
                  <Select
                    value={tagValue}
                    label="Metric"
                    displayEmpty
                    onChange={(e) => setTagValue(e.target.value)}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <em style={{ color: "#999" }}>--- Choose Metric ---</em>;
                      }
                      return getMetricLabel(selected);
                    }}
                    sx={{ minHeight: "56px" }}
                  >
                    {METRICS.map((metric) => (
                      <MenuItem key={metric.value} value={metric.value}>
                        {metric.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel shrink>Location</InputLabel>
                  <Select
                    value={groupId}
                    label="Location"
                    displayEmpty
                    onChange={(e) => setGroupId(e.target.value)}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <em style={{ color: "#999" }}>--- Choose Location ---</em>;
                      }
                      return getLocationLabel(selected);
                    }}
                    sx={{ minHeight: "56px" }}
                  >
                    {LOCATIONS.map((location) => (
                      <MenuItem key={location.value} value={location.value}>
                        {location.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSearch}
                  disabled={!tagValue || !groupId || isLoading}
                  startIcon={<SearchIcon />}
                  sx={{
                    backgroundColor: "#0a2240",
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#0d2d52" },
                    height: "56px",
                  }}
                >
                  {isLoading ? "Searching..." : "Search"}
                </Button>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleDownloadCSV}
                  disabled={!tagValue || !groupId || !responseData || isDownloading}
                  startIcon={<DownloadIcon />}
                  sx={{
                    borderColor: "#0a2240",
                    color: "#0a2240",
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#0d2d52",
                      backgroundColor: "rgba(10, 34, 64, 0.04)",
                    },
                    height: "56px",
                  }}
                >
                  {isDownloading ? "Downloading..." : "Download CSV"}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </FilterCard>
      </Box>

      {/* Error Display */}
      {isError && (
        <Box sx={{ padding: { xs: "0.3rem", sm: "1.3vw" }, paddingTop: 0 }}>
          <Alert severity="error" sx={{ borderRadius: "14px" }}>
            Error loading data: {error?.message || "Unknown error"}
          </Alert>
        </Box>
      )}

      {/* Search and Filter Section */}
      {responseData?.data && responseData.data.length > 0 && (
        <Box sx={{ padding: { xs: "0.3rem", sm: "1.3vw" }, paddingTop: 0 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }} sx={{ marginBottom: "24px" }}>
            <StyledChip
              label="Filter results"
              sx={{
                backgroundColor: "#E3F2FD",
                color: "#0a2240",
              }}
            />
            <TextField
              fullWidth
              placeholder="Search host, metric, value..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#666" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: { xs: "100%", sm: "500px" } }}
            />
          </Stack>

          {/* Host Filter */}
          {uniqueHosts.length > 1 && (
            <Box
              sx={{
                marginBottom: "24px",
                maxHeight: "150px",
                overflowY: "auto",
                padding: "8px",
                backgroundColor: "#fff",
                borderRadius: "14px",
              }}
            >
              <HostToggleGroup
                value={selectedHost}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue !== null) setSelectedHost(newValue);
                }}
              >
                {uniqueHosts.map((host) => (
                  <ToggleButton key={host} value={host}>
                    {host}
                  </ToggleButton>
                ))}
              </HostToggleGroup>
            </Box>
          )}
        </Box>
      )}

      {/* Loading State */}
      <BusyLoader open={isLoading} />

      {/* Data Cards */}
      <Box sx={{ padding: { xs: "0.3rem", sm: "1.3vw" }, paddingTop: 0 }}>
        {isLoading ? (
          <DataCardsLoadingSkeleton />
        ) : filteredData.length > 0 ? (
          <Grid container spacing={3}>
            {filteredData.map((item, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <DataCard>
                  <CardContent>
                    <Stack spacing={2}>
                      {/* Host - show for all cases except when identity_key is "Hostname" */}
                      {responseData?.identity_key !== "Hostname" && (
                        <>
                          <Box>
                            <SectionLabel>Host</SectionLabel>
                            <StyledChip
                              label={item.host}
                              icon={<StorageIcon />}
                              sx={{
                                backgroundColor: "#E8F5E9",
                                color: "#2E7D32",
                              }}
                            />
                          </Box>
                          <Box sx={{ height: "1px", backgroundColor: "#E0E0E0" }} />
                        </>
                      )}

                      {/* Handle both data formats */}
                      {responseData?.identity_key ? (
                        // Format WITH identity_key: show identity and metrics array
                        <>
                          <Box>
                            <SectionLabel>{responseData.identity_key}</SectionLabel>
                            <StyledChip
                              label={item.identity}
                              sx={{
                                backgroundColor: "#E3F2FD",
                                color: "#0a2240",
                              }}
                            />
                          </Box>

                          <Box sx={{ height: "1px", backgroundColor: "#E0E0E0" }} />

                          <Box>
                            <SectionLabel>Metrics</SectionLabel>
                            <Box
                              sx={{
                                maxHeight: "200px",
                                overflowY: "auto",
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              {item.metrics && item.metrics.length > 0 ? (
                                item.metrics.map((metric, idx) => (
                                  <Box
                                    key={idx}
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "baseline",
                                      padding: "6px 10px",
                                      backgroundColor: "#f8f9fa",
                                      borderRadius: "6px",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "0.75rem",
                                        color: "#666",
                                        fontFamily: "Montserrat",
                                        flex: 1,
                                      }}
                                    >
                                      {metric.name}
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: "0.875rem",
                                        fontWeight: 600,
                                        color: "#0a2240",
                                        fontFamily: "Montserrat",
                                        minWidth: "60px",
                                        textAlign: "right",
                                      }}
                                    >
                                      {metric.value}
                                    </Typography>
                                  </Box>
                                ))
                              ) : (
                                <Typography
                                  sx={{
                                    fontSize: "0.875rem",
                                    color: "#666",
                                    fontFamily: "Montserrat",
                                  }}
                                >
                                  No metrics available
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </>
                      ) : (
                        // Format WITHOUT identity_key: show metric name and single value
                        <>
                          <Box>
                            <SectionLabel>Metric</SectionLabel>
                            <StyledChip
                              label={item.metric}
                              sx={{
                                backgroundColor: "#E3F2FD",
                                color: "#0a2240",
                              }}
                            />
                          </Box>

                          <Box sx={{ height: "1px", backgroundColor: "#E0E0E0" }} />

                          <Box>
                            <SectionLabel>Value</SectionLabel>
                            <StyledChip
                              label={item.value}
                              sx={{
                                backgroundColor: "#FFF3E0",
                                color: "#E65100",
                                fontSize: "1rem",
                              }}
                            />
                          </Box>
                        </>
                      )}
                    </Stack>
                  </CardContent>
                </DataCard>
              </Grid>
            ))}
          </Grid>
        ) : (
          responseData?.data &&
          responseData.data.length > 0 && (
            <Alert severity="info" sx={{ borderRadius: "14px" }}>
              No results found for the current filters. Try adjusting your search criteria.
            </Alert>
          )
        )}

        {/* No Data State */}
        {!isLoading && !responseData && (
          <Card
            sx={{
              textAlign: "center",
              padding: "48px",
              borderRadius: "14px",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Typography
              sx={{
                fontSize: "1.1rem",
                color: "#666",
                fontFamily: "Montserrat",
              }}
            >
              Select a metric and location, then click Search to view data.
            </Typography>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default DataRequest;
